import React, { useState, useEffect } from "react";
import {
  Layout,
  Sparkles,
  Palette,
  Eye,
  ArrowRight,
  Check,
  Building,
  BookTemplate,
  Tag,
  RefreshCw,
  Search,
  Grid,
  ChevronDown,
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  X,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API_BASE_URL } from "../../constants";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const INDUSTRY_COLORS = {
  Government: "bg-red-600",
  "Food & Beverage": "bg-orange-500",
  Legal: "bg-blue-900",
  "Real Estate": "bg-green-700",
  Healthcare: "bg-blue-500",
  Nonprofit: "bg-purple-600",
  Retail: "bg-amber-500",
  "Professional Services": "bg-slate-700",
};

const INDUSTRY_RING_COLORS = {
  Government: "ring-red-600",
  "Food & Beverage": "ring-orange-500",
  Legal: "ring-blue-900",
  "Real Estate": "ring-green-700",
  Healthcare: "ring-blue-500",
  Nonprofit: "ring-purple-600",
  Retail: "ring-amber-500",
  "Professional Services": "ring-slate-700",
};

const IndustryBadge = ({ industry }) => {
  const bg = INDUSTRY_COLORS[industry] || "bg-slate-600";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${bg}`}
    >
      {industry}
    </span>
  );
};

const DocTypePill = ({ type }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
    {type}
  </span>
);

const BrandSwatch = ({ color }) => (
  <span
    className="inline-block w-4 h-4 rounded-full border border-slate-300"
    style={{ backgroundColor: color || "#6b7280" }}
    title={color}
  />
);

const EMPTY_FORM = {
  template_code: "",
  template_name: "",
  description: "",
  industry: "",
  vertical_id: "",
  facility_type: "informational",
  config: "{}",
  sample_documents: "[]",
  is_active: true,
};

// ── Main Component ─────────────────────────────────────────────────────────────

const KBTemplates = () => {
  const { token, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState("marketplace");
  const [templates, setTemplates] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [applyTemplate, setApplyTemplate] = useState(null);
  const [applyForm, setApplyForm] = useState({
    facility_id: "",
    agent_name: "",
    brand_color: "#cc1111",
    display_name: "",
    state: "",
  });
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // Manage tab state
  const [editingTemplate, setEditingTemplate] = useState(null); // null = new, obj = edit
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    fetchTemplates();
    fetchFacilities();
  }, [token]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/kb-templates`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to load KB templates:", err);
    }
    setLoading(false);
  };

  const fetchFacilities = async () => {
    try {
      const res = await fetch(`${AUTH_URL}/facilities`, { headers });
      if (res.ok) {
        const data = await res.json();
        setFacilities(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to load facilities:", err);
    }
  };

  // ── Apply template ───────────────────────────────────────────────────────────

  const handleApplyClick = (template) => {
    const config = parseConfig(template);
    setApplyTemplate(template);
    setApplyForm({
      facility_id: "",
      agent_name: config.agent_name || "",
      brand_color: config.brand_color || "#cc1111",
      display_name: "",
      state: "",
    });
    setApplyResult(null);
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!applyTemplate) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/apply-kb-template`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          template_code: applyTemplate.template_code,
          facility_id: applyForm.facility_id,
          overrides: {
            agent_name: applyForm.agent_name,
            brand_color: applyForm.brand_color,
            display_name: applyForm.display_name,
            state: applyForm.state,
          },
        }),
      });
      const data = await res.json();
      if (data.success) {
        setApplyResult(data);
      } else {
        setApplyResult({ error: data.error || "Failed to apply template" });
      }
    } catch (err) {
      setApplyResult({ error: "Network error applying template" });
    }
    setApplying(false);
  };

  // ── CRUD helpers ─────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingTemplate(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    setForm({
      template_code: t.template_code || "",
      template_name: t.template_name || "",
      description: t.description || "",
      industry: t.industry || "",
      vertical_id: t.vertical_id || "",
      facility_type: t.facility_type || "informational",
      config:
        typeof t.config === "string"
          ? t.config
          : JSON.stringify(t.config || {}, null, 2),
      sample_documents:
        typeof t.sample_documents === "string"
          ? t.sample_documents
          : JSON.stringify(t.sample_documents || [], null, 2),
      is_active: t.is_active !== false,
    });
    setFormError("");
    setShowForm(true);
  };

  const handleFormSave = async (e) => {
    e.preventDefault();
    setFormError("");
    setSaving(true);

    let configParsed, sampleParsed;
    try {
      configParsed = JSON.parse(form.config || "{}");
    } catch {
      setFormError("Config is not valid JSON");
      setSaving(false);
      return;
    }
    try {
      sampleParsed = JSON.parse(form.sample_documents || "[]");
    } catch {
      setFormError("Sample Documents is not valid JSON");
      setSaving(false);
      return;
    }

    const body = {
      ...form,
      config: configParsed,
      sample_documents: sampleParsed,
    };

    try {
      const isEdit = !!editingTemplate;
      const url = isEdit
        ? `${AUTH_URL}/kb-templates/${editingTemplate.id}`
        : `${AUTH_URL}/kb-templates`;
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success || data.id) {
        setShowForm(false);
        fetchTemplates();
      } else {
        setFormError(data.error || "Save failed");
      }
    } catch (err) {
      setFormError("Network error");
    }
    setSaving(false);
  };

  const handleToggleActive = async (t) => {
    try {
      const res = await fetch(`${AUTH_URL}/kb-templates/${t.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ is_active: !t.is_active }),
      });
      if (res.ok) fetchTemplates();
    } catch (err) {
      console.error("Toggle failed:", err);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `${AUTH_URL}/kb-templates/${deleteTarget.id}/delete`,
        { method: "POST", headers },
      );
      if (res.ok) {
        setDeleteTarget(null);
        fetchTemplates();
      }
    } catch (err) {
      console.error("Delete failed:", err);
    }
    setDeleting(false);
  };

  // ── Derived state ────────────────────────────────────────────────────────────

  const industries = [
    ...new Set(templates.map((t) => t.industry).filter(Boolean)),
  ];

  const filteredTemplates = templates.filter((t) => {
    if (!t.is_active) return false;
    const matchesSearch =
      !searchTerm ||
      (t.template_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (t.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.industry || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesIndustry = !industryFilter || t.industry === industryFilter;
    return matchesSearch && matchesIndustry;
  });

  const parseConfig = (template) => {
    if (!template.config) return {};
    if (typeof template.config === "string") {
      try {
        return JSON.parse(template.config);
      } catch {
        return {};
      }
    }
    return template.config;
  };

  const parseSampleDocs = (template) => {
    if (!template.sample_documents) return [];
    if (typeof template.sample_documents === "string") {
      try {
        return JSON.parse(template.sample_documents);
      } catch {
        return [];
      }
    }
    return Array.isArray(template.sample_documents)
      ? template.sample_documents
      : [];
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <Layout className="w-5 h-5 text-slate-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              KB Templates
            </h2>
            <p className="text-sm text-slate-500">
              Industry starter templates for fast facility provisioning
            </p>
          </div>
        </div>
        <button
          onClick={fetchTemplates}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      {isAdmin && (
        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab("marketplace")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === "marketplace"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Marketplace
          </button>
          <button
            onClick={() => setActiveTab("manage")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === "manage"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
          >
            Manage Templates
          </button>
        </div>
      )}

      {/* ── Marketplace Tab ── */}
      {activeTab === "marketplace" && (
        <>
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search templates by name, description, or industry..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="relative">
              <select
                value={industryFilter}
                onChange={(e) => setIndustryFilter(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All Industries</option>
                {industries.map((ind) => (
                  <option key={ind} value={ind}>
                    {ind}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin mr-3" />
              <span className="text-slate-500">Loading templates...</span>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
              <Grid className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No templates found</p>
              <p className="text-sm text-slate-400 mt-1">
                {searchTerm || industryFilter
                  ? "Try adjusting your search or filter criteria"
                  : "No active templates available yet"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => {
                const config = parseConfig(template);
                const docTypes = config.doc_types || [];
                const ringColor =
                  INDUSTRY_RING_COLORS[template.industry] || "ring-slate-300";
                return (
                  <div
                    key={template.id}
                    className={`bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow ring-1 ring-transparent hover:${ringColor} flex flex-col`}
                  >
                    <div className="p-5 pb-3">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-base font-bold text-slate-900 leading-tight">
                          {template.template_name}
                        </h3>
                        <IndustryBadge industry={template.industry} />
                      </div>
                      <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                        {template.description || "No description provided."}
                      </p>
                      {config.agent_name && (
                        <div className="flex items-center space-x-2 mb-3">
                          <Sparkles className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700">
                            AI Agent:{" "}
                            <span className="font-medium">
                              {config.agent_name}
                            </span>
                          </span>
                        </div>
                      )}
                      {config.brand_color && (
                        <div className="flex items-center space-x-2 mb-3">
                          <Palette className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-700">Brand:</span>
                          <BrandSwatch color={config.brand_color} />
                          <span className="text-xs text-slate-400 font-mono">
                            {config.brand_color}
                          </span>
                        </div>
                      )}
                      {docTypes.length > 0 && (
                        <div className="flex items-center flex-wrap gap-1 mt-3">
                          <Tag className="w-3.5 h-3.5 text-slate-400 mr-1" />
                          {docTypes.map((dt, i) => (
                            <DocTypePill key={i} type={dt} />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-auto border-t border-slate-100 p-4 flex items-center justify-between">
                      <button
                        onClick={() => setPreviewTemplate(template)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        <Eye className="w-4 h-4 mr-1.5" />
                        Preview
                      </button>
                      <button
                        onClick={() => handleApplyClick(template)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                      >
                        Apply to Facility
                        <ArrowRight className="w-4 h-4 ml-1.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── Manage Tab (admin only) ── */}
      {activeTab === "manage" && isAdmin && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={openCreate}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw className="w-6 h-6 text-slate-400 animate-spin mr-3" />
              <span className="text-slate-500">Loading...</span>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                      Template
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                      Industry
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                      Code
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                      Type
                    </th>
                    <th className="text-center px-4 py-3 font-medium text-slate-600">
                      Active
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {templates.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-10 text-center text-slate-400"
                      >
                        No templates yet
                      </td>
                    </tr>
                  ) : (
                    templates.map((t) => (
                      <tr
                        key={t.id}
                        className={`hover:bg-slate-50 ${!t.is_active ? "opacity-50" : ""}`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">
                            {t.template_name}
                          </p>
                          {t.description && (
                            <p className="text-xs text-slate-400 line-clamp-1 mt-0.5">
                              {t.description}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <IndustryBadge industry={t.industry} />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {t.template_code}
                        </td>
                        <td className="px-4 py-3 text-slate-500 capitalize">
                          {t.facility_type || "—"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleToggleActive(t)}
                            className="text-slate-400 hover:text-slate-700"
                            title={t.is_active ? "Deactivate" : "Activate"}
                          >
                            {t.is_active ? (
                              <ToggleRight className="w-5 h-5 text-green-500" />
                            ) : (
                              <ToggleLeft className="w-5 h-5" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => openEdit(t)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(t)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Preview Modal ── */}
      {previewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto mx-4">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center space-x-3">
                <BookTemplate className="w-5 h-5 text-slate-600" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Template Preview
                </h3>
              </div>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">
                  {previewTemplate.template_name}
                </h4>
                <div className="flex items-center flex-wrap gap-2 mb-3">
                  <IndustryBadge industry={previewTemplate.industry} />
                  {previewTemplate.vertical_id && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                      {previewTemplate.vertical_id}
                    </span>
                  )}
                  {previewTemplate.facility_type && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                      <Building className="w-3 h-3 mr-1" />
                      {previewTemplate.facility_type}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600">
                  {previewTemplate.description}
                </p>
              </div>
              {(() => {
                const config = parseConfig(previewTemplate);
                return (
                  <>
                    {config.system_prompt && (
                      <div>
                        <h5 className="text-sm font-semibold text-slate-700 mb-2">
                          System Prompt Template
                        </h5>
                        <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono overflow-x-auto max-h-48 overflow-y-auto">
                          {config.system_prompt}
                        </pre>
                      </div>
                    )}
                    {config.enrichment_rules &&
                      (Array.isArray(config.enrichment_rules)
                        ? config.enrichment_rules.length > 0
                        : true) && (
                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">
                            Enrichment Rules
                          </h5>
                          <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono overflow-x-auto max-h-48 overflow-y-auto">
                            {typeof config.enrichment_rules === "string"
                              ? config.enrichment_rules
                              : JSON.stringify(
                                  config.enrichment_rules,
                                  null,
                                  2,
                                )}
                          </pre>
                        </div>
                      )}
                    {config.doc_types && config.doc_types.length > 0 && (
                      <div>
                        <h5 className="text-sm font-semibold text-slate-700 mb-2">
                          Document Types
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {config.doc_types.map((dt, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                            >
                              <Tag className="w-3 h-3 mr-1.5" />
                              {dt}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(config.agent_name || config.brand_color) && (
                      <div>
                        <h5 className="text-sm font-semibold text-slate-700 mb-2">
                          Agent Configuration
                        </h5>
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                          {config.agent_name && (
                            <div className="flex items-center space-x-2">
                              <Sparkles className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-600">
                                Agent Name:
                              </span>
                              <span className="text-sm font-medium text-slate-900">
                                {config.agent_name}
                              </span>
                            </div>
                          )}
                          {config.brand_color && (
                            <div className="flex items-center space-x-2">
                              <Palette className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-600">
                                Brand Color:
                              </span>
                              <BrandSwatch color={config.brand_color} />
                              <span className="text-xs text-slate-500 font-mono">
                                {config.brand_color}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
              {(() => {
                const sampleDocs = parseSampleDocs(previewTemplate);
                if (sampleDocs.length === 0) return null;
                return (
                  <div>
                    <h5 className="text-sm font-semibold text-slate-700 mb-2">
                      Sample Documents
                    </h5>
                    <div className="space-y-2">
                      {sampleDocs.map((doc, i) => (
                        <div
                          key={i}
                          className="flex items-center space-x-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2"
                        >
                          <Layout className="w-4 h-4 text-slate-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {typeof doc === "string"
                                ? doc
                                : doc.name || doc.title || `Document ${i + 1}`}
                            </p>
                            {typeof doc === "object" && doc.description && (
                              <p className="text-xs text-slate-500 truncate">
                                {doc.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
              <button
                onClick={() => setPreviewTemplate(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleApplyClick(previewTemplate);
                  setPreviewTemplate(null);
                }}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700"
              >
                Apply to Facility
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Apply Modal ── */}
      {applyTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto mx-4">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center space-x-3">
                <ArrowRight className="w-5 h-5 text-slate-600" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Apply Template
                </h3>
              </div>
              <button
                onClick={() => {
                  setApplyTemplate(null);
                  setApplyResult(null);
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {applyResult?.success ? (
              <div className="p-6 space-y-5">
                <div className="flex items-center space-x-3 bg-green-50 border border-green-200 rounded-lg p-4">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Template applied successfully!
                    </p>
                    <p className="text-xs text-green-600 mt-0.5">
                      Facility ID: {applyResult.facility_id}
                    </p>
                  </div>
                </div>
                {applyResult.embed_snippet && (
                  <div>
                    <h5 className="text-sm font-semibold text-slate-700 mb-2">
                      Embed Snippet
                    </h5>
                    <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-700 whitespace-pre-wrap font-mono overflow-x-auto">
                      {applyResult.embed_snippet}
                    </pre>
                    <button
                      onClick={() =>
                        navigator.clipboard.writeText(applyResult.embed_snippet)
                      }
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Copy to clipboard
                    </button>
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setApplyTemplate(null);
                      setApplyResult(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleApplySubmit} className="p-6 space-y-5">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {applyTemplate.template_name}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {applyTemplate.template_code}
                      </p>
                    </div>
                    <IndustryBadge industry={applyTemplate.industry} />
                  </div>
                </div>
                {applyResult?.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700">{applyResult.error}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Target Facility *
                  </label>
                  <div className="relative">
                    <select
                      required
                      value={applyForm.facility_id}
                      onChange={(e) =>
                        setApplyForm({
                          ...applyForm,
                          facility_id: e.target.value,
                        })
                      }
                      className="w-full appearance-none pl-3 pr-8 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    >
                      <option value="">Select a facility...</option>
                      {facilities.map((f) => (
                        <option
                          key={f.id || f.facility_id}
                          value={f.facility_id}
                        >
                          {f.facility_name || f.display_name || f.facility_id}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Agent Name
                  </label>
                  <input
                    type="text"
                    value={applyForm.agent_name}
                    onChange={(e) =>
                      setApplyForm({ ...applyForm, agent_name: e.target.value })
                    }
                    placeholder="e.g. Lidy, Ben, Cal"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Brand Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={applyForm.brand_color}
                      onChange={(e) =>
                        setApplyForm({
                          ...applyForm,
                          brand_color: e.target.value,
                        })
                      }
                      className="w-10 h-10 rounded-lg border border-slate-300 cursor-pointer p-0.5"
                    />
                    <input
                      type="text"
                      value={applyForm.brand_color}
                      onChange={(e) =>
                        setApplyForm({
                          ...applyForm,
                          brand_color: e.target.value,
                        })
                      }
                      placeholder="#cc1111"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={applyForm.display_name}
                    onChange={(e) =>
                      setApplyForm({
                        ...applyForm,
                        display_name: e.target.value,
                      })
                    }
                    placeholder="e.g. City of Springfield"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    State *
                  </label>
                  <input
                    type="text"
                    required
                    value={applyForm.state}
                    onChange={(e) =>
                      setApplyForm({
                        ...applyForm,
                        state: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="e.g. AZ, TX, FL"
                    maxLength={2}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setApplyTemplate(null);
                      setApplyResult(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={applying}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50"
                  >
                    {applying ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Apply Template
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── Create / Edit Template Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingTemplate ? "Edit Template" : "New Template"}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleFormSave} className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Template Code *
                  </label>
                  <input
                    required
                    type="text"
                    value={form.template_code}
                    onChange={(e) =>
                      setForm({ ...form, template_code: e.target.value })
                    }
                    placeholder="e.g. political_az, restaurant_basic"
                    disabled={!!editingTemplate}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Template Name *
                  </label>
                  <input
                    required
                    type="text"
                    value={form.template_name}
                    onChange={(e) =>
                      setForm({ ...form, template_name: e.target.value })
                    }
                    placeholder="e.g. Arizona Political Campaign"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Industry *
                  </label>
                  <input
                    required
                    type="text"
                    value={form.industry}
                    onChange={(e) =>
                      setForm({ ...form, industry: e.target.value })
                    }
                    placeholder="e.g. Government, Healthcare, Legal"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Facility Type
                  </label>
                  <select
                    value={form.facility_type}
                    onChange={(e) =>
                      setForm({ ...form, facility_type: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="informational">Informational</option>
                    <option value="transactional">Transactional</option>
                    <option value="support">Support</option>
                    <option value="political">Political</option>
                    <option value="internal">Internal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Vertical ID
                  </label>
                  <input
                    type="text"
                    value={form.vertical_id}
                    onChange={(e) =>
                      setForm({ ...form, vertical_id: e.target.value })
                    }
                    placeholder="e.g. political, restaurant"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center space-x-3 pt-6">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: e.target.checked })
                    }
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label
                    htmlFor="is_active"
                    className="text-sm font-medium text-slate-700"
                  >
                    Active (visible in marketplace)
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Brief description of what this template is for..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Config JSON
                  <span className="ml-2 text-xs text-slate-400 font-normal">
                    (agent_name, brand_color, system_prompt, enrichment_rules,
                    doc_types)
                  </span>
                </label>
                <textarea
                  rows={8}
                  value={form.config}
                  onChange={(e) => setForm({ ...form, config: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-blue-500 resize-y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Sample Documents JSON
                  <span className="ml-2 text-xs text-slate-400 font-normal">
                    (array of {"{ title, description, doc_type }"})
                  </span>
                </label>
                <textarea
                  rows={4}
                  value={form.sample_documents}
                  onChange={(e) =>
                    setForm({ ...form, sample_documents: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-blue-500 resize-y"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingTemplate ? "Save Changes" : "Create Template"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Deactivate Template
              </h3>
            </div>
            <p className="text-sm text-slate-600">
              This will deactivate{" "}
              <span className="font-medium">{deleteTarget.template_name}</span>.
              It will no longer appear in the marketplace. You can reactivate it
              at any time.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  "Deactivate"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KBTemplates;
